import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Copy, Check, ExternalLink, AlertTriangle } from 'lucide-react';
import { TrainingSetupInfo } from '@/mock/gpu-instance';

interface TrainingSetupInfoCardProps {
  setupInfo: TrainingSetupInfo;
}

const TrainingSetupInfoCard = ({ setupInfo }: TrainingSetupInfoCardProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Card className="border-green-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-green-600">
            <GraduationCap className="size-5" />
            Training Environment Ready
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-green-500/10 text-green-600">
              {setupInfo.framework} v{setupInfo.frameworkVersion}
            </Badge>
            <Badge variant="outline">{setupInfo.baseModel}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Setup Command */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Setup Command</Label>
          <div className="relative">
            <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all pr-12 font-mono">
              {setupInfo.setupCommand}
            </pre>
            <Button
              size="sm"
              variant="ghost"
              className="absolute top-1 right-1 h-8"
              onClick={() => handleCopy(setupInfo.setupCommand, 'command')}
            >
              {copiedField === 'command' ? (
                <Check className="size-4" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Jupyter Notebook URL */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Jupyter Notebook</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all pr-12">
                {setupInfo.notebookUrl}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(setupInfo.notebookUrl, 'notebook')}
              >
                {copiedField === 'notebook' ? (
                  <Check className="size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(setupInfo.notebookUrl, '_blank')}
            >
              <ExternalLink className="size-4 mr-1" />
              Open
            </Button>
          </div>
        </div>

        {/* Quick Start Guide */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Quick Start</Label>
          <div className="bg-muted/50 p-3 rounded-lg text-sm space-y-2">
            <p>1. Connect via SSH using the command above</p>
            <p>2. Run the setup command to activate your environment</p>
            <p>3. Open Jupyter Notebook to start training interactively</p>
            <p>4. Your model checkpoints will be saved to <code className="bg-muted px-1 rounded">./checkpoints</code></p>
          </div>
        </div>

        {/* Demo Warning */}
        <Alert className="border-yellow-500/50 bg-yellow-500/5">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            <span className="font-medium">Demo Mode:</span> This is simulated data.
            In production, the training environment will be fully configured.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default TrainingSetupInfoCard;
